import { useCallback, useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Checkbox,
  DropDown,
  FilterIcon,
  InputSearch,
  TableColumns,
  Text,
  Pagination,
  SettingIcon,
  TableColumnsColumnProps,
  Select,
  Tag,
} from '@mercai/clever'

import {
  TableColumnHostNumber,
  TableColumnRemoteNumber,
  TableColumnStatus,
  TableColumnMessages,
  TableColumnCreatedAt,
  TableColumnActions
} from '../table-columns'

import { ConversationDrawerFilter } from '../drawer-filter'

import { AppliedFilters } from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { useCookieState } from '@/helpers'
import { IAHostConversationProps } from '@/types'

import { ConversationTableProps } from './table.type'

import styles from './table.module.css'

export const HostConversationTable = ({
  data,
  isLoading,
  filter,
  onSetFilter,
  onHandleEditRegister,
  onHandleShowRegister,
}: ConversationTableProps) => {
  const { t } = useTranslation('screenLeadIntake')

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)
  const [totalPages, setTotalPages] = useState(1)

  const [columnsKeys, setColumnsKeys] = useCookieState<string[]>(
    `@mercai/host-conversation-columns`,
    [
      'hostNumber',
      'status',
      'messages',
      'createdAt',
      'actionsDevelopment',
    ],
  )

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) => !!filter[key] && key !== 'page' && key !== 'limit',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const onHandleColumnsShow = (key: string) => {
    let newColumnsKeys = columnsKeys

    if (columnsKeys.includes(key)) {
      newColumnsKeys = columnsKeys.filter((item) => item !== key)
    } else {
      newColumnsKeys = [...columnsKeys, key]
    }

    setColumnsKeys(newColumnsKeys)
  }

  const getColumnsShow = useCallback(
    (key: string) => columnsKeys.includes(key),
    [columnsKeys],
  )

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      onSetFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      onSetFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  useEffect(() => {
    if (data?.count) {
      setTotalPages(
        Math.ceil(data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  const columns: TableColumnsColumnProps<IAHostConversationProps>[] = [

    {
      title: t('conversationTableHostNumber'),
      key: 'hostNumber',
      dataKey: 'hostNumber',
      render: ({ record }) => <TableColumnHostNumber record={record} />,
    },
    {
      title: t('conversationTableRemoteNumber'),
      key: 'remoteNumber',
      dataKey: 'remoteNumber',
      render: ({ record }) => <TableColumnRemoteNumber record={record} />,
    },
    {
      title: t('conversationTableStatus'),
      width: '9rem',
      key: 'status',
      dataKey: 'status',
      render: ({ value }) => <TableColumnStatus value={value as string} />,
    },
    {
      title: t('conversationTableMessages'),
      key: 'messages',
      width: '10rem',
      dataKey: 'messages',
      render: ({ value }) => (
        <TableColumnMessages value={value as unknown as unknown[]} />
      ),
    },
    {
      title: t('conversationTableCreatedAt'),
      key: 'createdAt',
      width: '9rem',
      dataKey: 'createdAt',
      render: ({ value }) => (
        <TableColumnCreatedAt value={value as string | null} />
      ),
    },
    {
      title: t('conversationTableActions'),
      key: 'actionsDevelopment',
      dataKey: 'uuid',
      width: '16.5rem',
      render: ({ record }) => (
        <TableColumnActions
          record={record}
          onHandleEditRegister={onHandleEditRegister}
          onHandleShowRegister={onHandleShowRegister}
        />
      ),
    },
  ]

  const memoizedColumns = useMemo(() => {
    const columnsFiltered = columns?.reduce<
      TableColumnsColumnProps<IAHostConversationProps>[]
    >((acc, current) => {
      const column = columns?.find((column) => column.key === current.key)

      if (column && getColumnsShow(column.key)) {
        return [...acc, column]
      }

      return acc
    }, [])

    return columnsFiltered
  }, [columns, getColumnsShow])

  return (
    <div className={styles.core}>
      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('searchFilterPlaceholder'),
            }}
          />{' '}
          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('conversationTableFilterButton')}
            {getContentTotalFiltered()}
          </Button>
        </div>

        <div className={styles['filter-group']}>
          <DropDown.Root>
            <DropDown.ButtonTrigger
              variant="secondary"
              model="square"
              hasArrow={false}
            >
              <SettingIcon />
            </DropDown.ButtonTrigger>

            <DropDown.Content orientation="bottom-right">
              {columns?.map((column) => (
                <DropDown.Item key={column.key}>
                  <Checkbox
                    onChange={() => onHandleColumnsShow(column.key)}
                    value={getColumnsShow(column.key)}
                  >
                    {column.title}
                  </Checkbox>
                </DropDown.Item>
              ))}
            </DropDown.Content>
          </DropDown.Root>
        </div>
      </div>

      <AppliedFilters
        translations={{
          filterAppliedText: t('conversationTableFilterAppliedText'),
        }}
        filtersApplied={filter}
        onRemoveFilter={(key) => handleFilter(key, '')}
        keyTranslations={{
          search: t('conversationTableFilterSearch'),
        }}
        renderValue={(_key: string, value: string) => {
          return value
        }}
        excludeKeys={[
          'page',
          'limit',
          'uuid',
          'show',
          'form',
          'tab',
          'new',
        ]}
      />

      <TableColumns<IAHostConversationProps>
        isLoading={isLoading}
        columns={memoizedColumns}
        data={data?.results || []}
        classNames={{
          bodyColumn: styles['body-column'],
        }}
      />

      <div className={styles.pagination}>
        <div className={styles['pagination-show-registers']}>
          <Text>
            {t('conversationTableShowRegisters', {
              showNumber:
                (data?.count || 0) < +filter.limit
                  ? data?.count
                  : +filter.limit,
              totalNumber: data?.count || 0,
            })}
          </Text>

          <Select
            value={filter.limit}
            onChange={(value) => handleFilter('limit', value)}
            options={[
              { label: '10', value: '10' },
              { label: '20', value: '20' },
              { label: '50', value: '50' },
              { label: '100', value: '100' },
            ]}
          />
        </div>

        <Pagination
          totalPages={totalPages}
          onChange={(page) => handleFilter('page', page)}
          currentPage={+filter.page as number}
        />
      </div>

      <ConversationDrawerFilter
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
        filter={filter}
        onHandleFilter={onSetFilter}
      />
    </div>
  )
}
